import { useEffect, useState } from "react";
import api from "@/services/api";
import { MultiSelectOption } from "@/components/ui/MultiSelect";

interface RawCostCenter {
  id?: number;
  name?: string;
  code?: string;
  parentId?: number;
}

function extractList(data: unknown): RawCostCenter[] {
  if (Array.isArray(data)) return data as RawCostCenter[];
  if (data == null || typeof data !== "object") return [];
  const o = data as Record<string, unknown>;
  const keys = ["results", "data", "content", "items", "costCenters", "records", "elements"] as const;
  for (const key of keys) {
    const arr = o[key];
    if (Array.isArray(arr) && arr.length > 0) return arr as RawCostCenter[];
  }
  for (const key of keys) {
    const arr = o[key];
    if (Array.isArray(arr)) return arr as RawCostCenter[];
  }
  return [];
}

function toOption(cc: RawCostCenter): MultiSelectOption {
  const label = cc.code
    ? `${cc.code} – ${cc.name ?? ""}`.trim()
    : (cc.name ?? String(cc.id));
  return { value: String(cc.id ?? ""), label };
}

/**
 * Busca centros de custo do endpoint /api/cost-centers.
 * Retorna options prontas para uso no <MultiSelect> ou <FilterBar>.
 */
export function useCostCenters() {
  const [options, setOptions] = useState<MultiSelectOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState<string | null>(null);

  useEffect(() => {
    setLoading(true);
    setError(null);

    api
      .get("/api/cost-centers")
      .then(({ data }) => {
        const list = extractList(data);
        setOptions(list.map(toOption));
      })
      .catch((err: unknown) => {
        const d = (err as { response?: { data?: { detail?: string; error?: string } } })?.response?.data;
        const hint = [d?.detail, d?.error].filter(Boolean).join(" — ");
        setError(
          hint
            ? `Não foi possível carregar os centros de custo. (${hint})`
            : "Não foi possível carregar os centros de custo.",
        );
        setOptions([]);
      })
      .finally(() => setLoading(false));
  }, []);

  return { options, loading, error };
}
